import type { NavigateFunction } from "react-router-dom";
import type { UpsellVariant } from "@/components/UpsellContent";

/**
 * Single upgrade entry point used by every premium gate.
 *
 * Always navigates to the dedicated `/upgrade` route — no modal, no device
 * detection. The previous mobile-vs-desktop branching was unreliable on
 * iPhone Safari/Chrome (especially right after an email-verification
 * redirect, when the URL hash still contained auth tokens) and could leave
 * the Stripe iframe rendering as a blank backdrop. Routing to a real page
 * is the only path that works deterministically across iOS/Android/desktop.
 *
 * The legacy `openDesktopUpgrade` parameter is kept so existing callers
 * don't have to be rewritten in lockstep — it's intentionally ignored.
 */
export const openUpgrade = (
  _navigate: NavigateFunction,
  variant: UpsellVariant,
  _openDesktopUpgrade: () => void,
  fromPath?: string,
) => {
  const from = fromPath || window.location.pathname + window.location.search;
  const params = new URLSearchParams();
  if (variant !== "default") params.set("ctx", variant);
  params.set("from", from);
  // Give any open editors (e.g. RecipeCard) a synchronous chance to flush
  // their in-memory drafts to sessionStorage before we full-page redirect.
  // Listeners must do their work synchronously inside the event handler —
  // microtasks won't run before location.assign.
  try {
    window.dispatchEvent(new CustomEvent("cp:flush-edits"));
  } catch {
    // Older browsers without CustomEvent — non-fatal.
  }
  window.location.assign(`/upgrade?${params.toString()}`);
};
